/**
 * Estado global del tema. Tres modos:
 *   - "light"  → aplica data-theme="axorial"
 *   - "dark"   → aplica data-theme="axorial-dark"
 *   - "system" → sigue prefers-color-scheme y reacciona a cambios del SO
 *
 * Persiste en localStorage bajo "axorial.theme". El script en index.html
 * aplica el data-theme antes de que React monte para evitar flash.
 */
import { useCallback, useEffect, useState } from "react";

export type ThemeMode = "light" | "dark" | "system";

const STORAGE_KEY = "axorial.theme";
const LIGHT = "axorial";
const DARK = "axorial-dark";

function resolveSystem(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function readMode(): ThemeMode {
  if (typeof window === "undefined") return "system";
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === "light" || raw === "dark" || raw === "system") return raw;
  return "system";
}

function applyTheme(mode: ThemeMode) {
  const effective = mode === "system" ? resolveSystem() : mode;
  document.documentElement.setAttribute("data-theme", effective === "dark" ? DARK : LIGHT);
}

export function useTheme() {
  const [mode, setMode] = useState<ThemeMode>(readMode);

  useEffect(() => {
    applyTheme(mode);
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  // Si el modo es "system", re-aplicar cuando cambia la preferencia del SO.
  useEffect(() => {
    if (mode !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme("system");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [mode]);

  const cycle = useCallback(() => {
    setMode((m) => (m === "light" ? "dark" : m === "dark" ? "system" : "light"));
  }, []);

  return { mode, setMode, cycle, effective: mode === "system" ? resolveSystem() : mode };
}
